import PageHead from "../components/PageHead";
import CTA from "../components/CTA";

const STAGES = [
  ["01", "Train",
    [
      "Before Docket processes anything, you teach it. In the Document Training module you pick a document type, upload a set of representative samples, identify the fields that matter, and set a confidence threshold. Publishing turns that into a live model, and none of it asks for code or data-science skills.",
      "Because Docket is trained rather than generic, it only ever extracts what it has been taught to find. That is a deliberate design choice: a model that knows your documents will not improvise on one it has never seen.",
    ],
    ["Guided training UI", "Configurable threshold", "No data scientist"],
  ],
  ["02", "Classify",
    [
      <span key="c">
        Every uploaded file is matched against your trained models. When a document matches a model, it is routed to it automatically. When a document matches nothing, Docket holds it in a clear <em>no model match</em> state rather than guessing, so an untrained document type is surfaced, not silently mishandled.
      </span>,
    ],
    ["Automatic routing", "No-match handling", "Multi-model"],
  ],
  ["03", "Split",
    [
      "Real-world uploads are rarely one clean document. A scanned batch or an emailed packet can hold an invoice, a purchase order and a letter in a single file. Docket detects those boundaries, separates the file into sub-documents, and classifies each one independently, preserving page ranges so every split traces back to its source.",
      "This removes the manual pre-sorting step entirely, and guarantees the right extraction model runs on the right pages.",
    ],
    ["Boundary detection", "Per-document classification", "Page-range tracking"],
  ],
  ["04", "Extract",
    ["For each classified document, the trained model pulls the fields and line items it was taught to find, header fields, tables, totals, dates, references. Every extracted value carries its own confidence score, so the result is never a flat block of text but a structured, measurable record."],
    ["Field & line-item extraction", "Per-field confidence", "Structured output"],
  ],
  ["05", "Validate",
    [
      "Confidence scores decide what needs a person. Results above your threshold flow straight through. Anything below it is routed to a reviewer in a side-by-side workspace, extracted values on one side, the source document on the other, with bounding-box links connecting each field to the exact place it came from.",
      "Every correction a reviewer makes is captured and fed back into training, so the model improves with use. Review is treated as a feature of the pipeline, not a sign it failed.",
    ],
    ["Selective routing", "Side-by-side review", "Corrections retrain the model"],
  ],
  ["06", "Summarise",
    ["Alongside the structured data, Docket generates a plain-language summary of each document, the essence of a long contract or a dense form without reading every page. It gives downstream teams context, fast, without changing the extracted record."],
    ["Plain-language summary", "Per-document"],
  ],
  ["07", "Deliver",
    ["Verified, structured data leaves Docket in the shape your systems expect, CSV, XML, Excel or JSON for export, or a direct connection into the document management system or system of record you already run. Source files can be downloaded too, so nothing is locked in."],
    ["CSV · XML · Excel · JSON", "Direct DMS connection", "File download"],
  ],
];

export default function HowItWorks() {
  return (
    <>
      <PageHead
        crumb="How it works"
        eyebrow="How it works"
        title="One trained pipeline, from upload to delivery."
        lede="Every document Docket handles travels the same seven stages. It begins with training, because a platform that has learned your documents is one you can trust with them, and ends with structured data delivered exactly where your business needs it."
      />

      <section className="section--tight bg-2 bordered">
        <div className="container">
          <div className="pipe pipe--onsurface reveal">
            <div className="pipe__rail" aria-hidden="true" />
            {[
              ["01", "Train", "Teach your document types."],
              ["02", "Classify", "Match uploads to models."],
              ["03", "Split", "Separate mixed files."],
              ["04", "Extract", "Pull scored fields."],
              ["05", "Validate", "Review what's flagged."],
              ["06", "Summarise", "Generate a summary."],
              ["07", "Deliver", "Send structured data."],
            ].map(([n, t, d]) => (
              <div key={n} className="pstep"><div className="pstep__node">{n}</div><div className="pstep__name">{t}</div><div className="pstep__desc">{d}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="s-head reveal">
            <div className="eyebrow">Stage by stage</div>
            <h2>What happens to a document inside Docket.</h2>
          </div>
          <div className="stages">
            {STAGES.map(([n, name, paragraphs, tags]) => (
              <div key={n} className="stage reveal">
                <div className="stage__no">Stage<b>{n}</b></div>
                <div className="stage__body">
                  <h3>{name}</h3>
                  {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                  <div className="stage__tags">{tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-2 bordered">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>Underneath it all</div>
              <h2>A multi-tenant platform, and an audit trail that writes itself.</h2>
              <p>The seven stages run inside an isolated tenant, your own users, models, configuration, storage and data.</p>
              <p className="mark">Every step is captured with actor, timestamp and before-and-after state, so the audit trail is a by-product of the work, not a separate project.</p>
            </div>
            <div className="plist">
              {[
                ["Isolated by tenant", "Each customer organisation runs as its own tenant. No data, model or configuration is ever shared across tenants."],
                ["Logged at every stage", "Training, classification, splitting, extraction, validation and delivery are all recorded, who did what, when, and what changed."],
                ["Improves with use", "Reviewer corrections feed retraining, so the same pipeline gets measurably more accurate the longer it runs."],
              ].map(([h, p], i) => (
                <div key={h} className={`pitem reveal${i ? ` d${i}` : ""}`}>
                  <div className="pitem__no">→</div>
                  <div><h3>{h}</h3><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTA
        title='Watch the pipeline run on <em>your</em> documents.'
        text="In a pilot, we train Docket on two or three of your real document types and run them through all seven stages, so you see the accuracy and the automation gain on your own data."
      />
    </>
  );
}
